import type { ReactCalendarHeatmapValue } from 'react-calendar-heatmap';
import CalendarHeatmap from 'react-calendar-heatmap';
import { useEffect, useMemo, useState } from "react";
import { Box, Card, CardHeader } from "@mui/material";

import { UserStatisticsService } from "../../../api/user-statistics-service";

type HeatmapValue = ReactCalendarHeatmapValue<string>;

type ActivityValue = {
    date: string;
    count: number;
};

export type UserActivitiesProps = {
    title?: string;
};

// A `tooltipDataAttrs` callback used to emit a data-tooltip attribute here. Nothing read it
// (no tooltip library is installed) and titleForValue already renders the same text natively,
// so the inert prop was dropped instead of being typed around.

/**
 * The heatmap hands its callbacks a value whose extra fields are untyped, so the day's
 * workout count is narrowed back out here before it is used.
 */
function toActivity(value: HeatmapValue | undefined): ActivityValue | undefined {
    if (!value || typeof value.count !== 'number') return undefined;
    return { date: value.date, count: value.count };
}

function describe(value: HeatmapValue | undefined): string {
    const activity = toActivity(value);
    return activity?.count ? `${ activity.date }: ${ activity.count } workout(s)` : 'No workouts';
}

function colorClass(value: HeatmapValue | undefined): string {
    const activity = toActivity(value);

    if (!activity || !activity.count) return 'color-empty';
    if (activity.count < 3) return 'color-scale-1';
    if (activity.count < 5) return 'color-scale-2';
    if (activity.count < 7) return 'color-scale-3';
    return 'color-scale-4';
}

export default function UserActivities({ title, ...other }: UserActivitiesProps) {
    const [ calendarData, setCalendarData ] = useState<ActivityValue[]>([]);

    // Dates are memoised so the heatmap does not get fresh Date objects on every render
    const { today, startDate } = useMemo(() => {
        const today = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 1.2 * 365);
        startDate.setDate(startDate.getDate() - startDate.getDay());

        return { today, startDate };
    }, []);

    useEffect(() => {
        UserStatisticsService.userCalendar()
            .then((response) => {
                setCalendarData(response.items.map(item => ({
                    date: item.date,
                    count: item.workouts
                })));
            })
            .catch((error) => console.log('Failed to load user calendar', error));
    }, []);

    return <>
        <Card { ...other }>
            <CardHeader title={ title } subheader=""/>

            <Box sx={ { p: 3, pb: 1 } } dir="ltr">
                <CalendarHeatmap
                    startDate={ startDate }
                    endDate={ today }
                    values={ calendarData }
                    titleForValue={ describe }
                    classForValue={ colorClass }/>
            </Box>
        </Card>
    </>;
}
